import { navigateTo, useCookie } from "#imports";
import { useQueryCurrentPage } from "@voix/composables/queries/useQueryCurrentPage";
import { useAdminStore } from "@voix/store/adminStore";
import { useSliceStore } from "@voix/store/sliceStore";
import { usePageStore } from "../store/pageStore.js";
import { useReleases } from "./useReleases.js";
function moveElementTopOfPage(elementId) {
  const pageStore = usePageStore();
  const currentPage = pageStore.currentPage;
  const foundElement = pageStore.findElement(elementId);
  if (foundElement && currentPage) {
    const { extractionElements, extractionIndex } = getExtractionDetails(foundElement);
    if (extractionElements !== null && extractionIndex !== null) {
      const splicedElement = extractionElements.splice(extractionIndex, 1);
      currentPage.elements.splice(0, 0, splicedElement[0]);
    }
  }
}
function moveElementTopOfGroup(elementId, group) {
  const pageStore = usePageStore();
  const currentPage = pageStore.currentPage;
  const foundElement = pageStore.findElement(elementId);
  if (foundElement && currentPage) {
    const { extractionElements, extractionIndex } = getExtractionDetails(foundElement);
    const nestingCheckFailed = isElementNestingInsideItself(
      foundElement.element,
      group
    );
    if (extractionElements !== null && extractionIndex !== null && !nestingCheckFailed) {
      const splicedElement = extractionElements.splice(extractionIndex, 1);
      group.elements.splice(0, 0, splicedElement[0]);
    }
  }
}
function moveElementTopOfSlice(elementId, slice) {
  const pageStore = usePageStore();
  const sliceStore = useSliceStore();
  const currentPage = pageStore.currentPage;
  const foundElement = pageStore.findElement(elementId);
  const sliceConfig = sliceStore.getSlice(slice.component);
  if (sliceConfig?.slots && sliceConfig?.slots.length) {
    if (foundElement && currentPage) {
      const { extractionElements, extractionIndex } = getExtractionDetails(foundElement);
      const nestingCheckFailed = isElementNestingInsideItself(
        foundElement.element,
        slice
      );
      if (extractionElements !== null && extractionIndex !== null && !nestingCheckFailed) {
        const splicedElement = extractionElements.splice(extractionIndex, 1);
        if (!slice.elements)
          slice.elements = [];
        slice.elements.splice(0, 0, splicedElement[0]);
      }
    }
  }
}
function moveElementBelowElement(elementId, elementAbove) {
  const pageStore = usePageStore();
  const foundElement = pageStore.findElement(elementId);
  const foundElementAbove = pageStore.findElement(elementAbove.id);
  const currentPage = pageStore.currentPage;
  if (foundElement && foundElementAbove && currentPage) {
    const { extractionElements, extractionIndex } = getExtractionDetails(foundElement);
    if (extractionElements !== null && extractionIndex !== null) {
      if (foundElementAbove.parentElement === null) {
        const injectionElements = currentPage.elements;
        const injectionIndex = injectionElements.indexOf(
          foundElementAbove.element
        );
        const splicedElement = extractionElements.splice(extractionIndex, 1);
        const finalInjectionIndex = injectionIndex <= extractionIndex ? injectionIndex + 1 : injectionIndex;
        injectionElements.splice(finalInjectionIndex, 0, splicedElement[0]);
      }
      if (foundElementAbove.parentElement && foundElementAbove.parentElement.elements) {
        const injectionElements = foundElementAbove.parentElement.elements;
        const injectionIndex = injectionElements.indexOf(
          foundElementAbove.element
        );
        const nestingCheckFailed = isElementNestingInsideItself(
          foundElement.element,
          foundElementAbove.parentElement
        );
        if (!nestingCheckFailed) {
          const splicedElement = extractionElements.splice(extractionIndex, 1);
          injectionElements.splice(injectionIndex, 0, splicedElement[0]);
        }
      }
    }
  }
}
function getExtractionDetails(foundElement) {
  const pageStore = usePageStore();
  const currentPage = pageStore.currentPage;
  if (foundElement.parentElement === null) {
    const extractionElements = currentPage.elements;
    const extractionIndex = extractionElements.indexOf(foundElement.element);
    return { extractionElements, extractionIndex };
  }
  if (foundElement.parentElement && foundElement.parentElement.elements) {
    const extractionElements = foundElement.parentElement.elements;
    const extractionIndex = extractionElements.indexOf(foundElement.element);
    return { extractionElements, extractionIndex };
  }
  return { extractionElements: null, extractionIndex: null };
}
function isElementNestingInsideItself(element, attemptedParent) {
  if (element.id === attemptedParent.id)
    return true;
  return false;
}
function alterElementTypes(pageData) {
  pageData.elements.map((element) => {
    if (Object.prototype.hasOwnProperty.call(element, "model_query") && element.model_query !== null)
      element.type = "query";
    return element;
  });
  return pageData;
}
async function requestSave(save) {
  const pageStore = usePageStore();
  if (pageStore.isSaving) {
    return;
  }
  try {
    pageStore.setIsSaving(true);
    let pageData = JSON.parse(JSON.stringify(pageStore.currentPage));
    pageData = alterElementTypes(pageData);
    const { setReleaseData } = useReleases();
    const adminStore = useAdminStore();
    let releaseId = adminStore.releaseId;
    let releaseDetachFromId = "";
    if (releaseId) {
      if (releaseId !== pageData.current_release.id && releaseId !== pageData.draft_release?.id)
        releaseId = "";
      if (save.saveMode.value === "queue-new-release" || save.saveMode.value === "publish") {
        releaseDetachFromId = releaseId;
        releaseId = "";
      }
    }
    const payload = setReleaseData(save, {
      id: pageData.id,
      title: pageData.title,
      path: pageData.path,
      fields: pageData.fields,
      layout: pageData.layout,
      elements: pageData.elements,
      tags: pageData.tags,
      release_date: "",
      release_label: "",
      release_id: releaseId || "",
      release_status: "unscheduled",
      release_detach_from_id: releaseDetachFromId
    });
    const { updateCurrentPage } = useQueryCurrentPage();
    const updateResponse = await updateCurrentPage(payload);
    if (updateResponse.data?.value.current_release?.id) {
      const releasePreviewCookie = useCookie("voix-release-preview");
      releasePreviewCookie.value = updateResponse.data.value.current_release.id;
    }
    navigateTo({
      name: "voix-studio-en",
      query: {
        editing: pageData.path
      }
    });
    const releaseSaveModes = [
      "queue-new-release",
      "queue-existing-release"
    ];
    if (releaseId && releaseSaveModes.includes(save.saveMode.value))
      adminStore.setReleaseId(save.releaseId.value);
    else if (pageStore.currentPage?.draft_release?.id || save.saveMode.value === "publish")
      adminStore.unsetReleaseId();
    adminStore.unselectElements();
    pageStore.setIsSaving(false);
    save.endSave(save.close);
  } catch (error) {
    console.error("Error saving page", { error });
    pageStore.setIsSaving(false);
    save.endSave(save.close);
  }
}
async function requestSaveDraft(save) {
  const pageStore = usePageStore();
  try {
    let pageData = JSON.parse(JSON.stringify(pageStore.currentPage));
    pageData = alterElementTypes(pageData);
    pageData.release_status = "draft";
    const payload = Object.assign({}, pageData);
    const { updateCurrentPage } = useQueryCurrentPage();
    const { data } = await updateCurrentPage(payload);
    navigateTo({
      name: "voix-studio-en",
      query: {
        editing: pageData.path
      }
    });
    const adminStore = useAdminStore();
    const releaseResponse = data.value;
    const releaseId = releaseResponse.draft_release.id;
    if (releaseId) {
      const releasePreviewCookie = useCookie("voix-release-preview");
      releasePreviewCookie.value = releaseId;
      adminStore.setReleaseId(releaseId);
    } else {
      adminStore.unsetReleaseId();
    }
    adminStore.unselectElements();
    pageStore.setIsSaving(false);
    save.endSave(save.close);
  } catch (error) {
    console.error("Error saving page", { error });
    pageStore.setIsSaving(false);
    save.endSave(save.close);
  }
}
export {
  moveElementBelowElement,
  moveElementTopOfGroup,
  moveElementTopOfPage,
  moveElementTopOfSlice,
  requestSave,
  requestSaveDraft
};
